import React from 'react';
import {
  Badge, BadgeText, HStack, Spinner,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import type { ScheduledChangeVisibleState } from '@/common/api/models';

type ChipStyle = {
  bg: string;
  color: string;
  borderWidth?: number;
  borderColor?: string;
};

const CHIP_STYLES: Record<ScheduledChangeVisibleState, ChipStyle> = {
  scheduled: { bg: '$statusInfoSubtle', color: '$textOnInfoSubtle' },
  draft_ready: { bg: '$statusInfoSubtle', color: '$textOnInfoSubtle' },
  applying: { bg: '$statusInfoSubtle', color: '$textOnInfoSubtle' },
  live: { bg: '$statusSuccessSubtle', color: '$textOnSuccessSubtle' },
  failed: { bg: '$statusWarningSubtle', color: '$textOnWarningSubtle' },
  superseded: {
    bg: '$bgSubtle',
    color: '$textSubtle',
    borderWidth: 1,
    borderColor: '$borderLight',
  },
  cancelled: {
    bg: '$bgSubtle',
    color: '$textSubtle',
    borderWidth: 1,
    borderColor: '$borderLight',
  },
};

interface Props {
  state: ScheduledChangeVisibleState;
  kind?: string;
  /** Stored stand-down error on working-mode rows (`author_took_over`, `draft_removed`). */
  standDownReason?: string | null;
}

function chipLabelKey(state: ScheduledChangeVisibleState, kind?: string, standDownReason?: string | null): string {
  if (
    state === 'superseded'
    && kind === 'working_mode_switch'
    && (standDownReason === 'author_took_over' || standDownReason === 'draft_removed')
  ) {
    return 'lecturer.scheduledChanges.state.workingModeNotPublished';
  }
  return `lecturer.scheduledChanges.state.${state}`;
}

export const ScheduledChangeStateChip: React.FC<Props> = ({
  state,
  kind,
  standDownReason,
}) => {
  const { t } = useTranslation();
  const style = CHIP_STYLES[state];
  const label = t(chipLabelKey(state, kind, standDownReason));

  return (
    <Badge
      testID="scheduled-change-state-chip"
      bg={style.bg}
      borderRadius="$sm"
      borderWidth={style.borderWidth ?? 0}
      borderColor={style.borderColor}
      px="$2"
      py="$0.5"
    >
      <HStack alignItems="center" space="xs">
        {state === 'applying' && <Spinner size={10} color={style.color} />}
        <BadgeText color={style.color} size="xs">
          {label}
        </BadgeText>
      </HStack>
    </Badge>
  );
};
